import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import "./VNextToast.css";

export type VNextToastNotification = {
  id: string;
  /** Authored, safe product copy. Never pass a server error's raw message. */
  message: string;
} & ({ tone: "success"; onUndo?: () => void } | { tone: "error" });

export function VNextToast({
  notification,
  onDismiss,
}: {
  notification: VNextToastNotification | null;
  onDismiss: () => void;
}) {
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);
  const id = notification?.id;
  const tone = notification?.tone;
  useEffect(() => {
    if (!id || tone !== "success") return undefined;
    const timer = window.setTimeout(() => dismiss.current(), 4000);
    return () => window.clearTimeout(timer);
  }, [id, tone]);

  if (!notification) return null;
  return createPortal(
    <div
      className={`vnext-toast vnext-toast--${notification.tone}`}
      role={notification.tone === "error" ? "alert" : "status"}
      aria-atomic="true"
    >
      <span>{notification.message}</span>
      {notification.tone === "success" && notification.onUndo && (
        <button
          type="button"
          onClick={() => {
            notification.onUndo?.();
            onDismiss();
          }}
        >
          Undo
        </button>
      )}
      {notification.tone === "error" && (
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={onDismiss}
        >
          ×
        </button>
      )}
    </div>,
    document.body,
  );
}
